// Styles
import { FontSizes, FontWeights, keyframes, memoizeFunction } from "@fluentui/react";
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { CardShadow, LoopBorderRadius, PlannerBorderRadius } from "../../../../constants/StyleConstants";
import { highContrastBorders, highContrastColor, highContrastHighlightColor, highContrastSelectionText } from "../../../../utilities/HighContrast.styles";
import { backgroundColorHover, backgroundColorNeutralPrimary, backgroundColorNeutralSecondary, backgroundColorNeutralSecondaryAlt, flexChildSizeFixed, flexChildSizeFlexible, flexParent, flexParentCenterAll, flexParentCenterCrossAxis, fontColorNeutralPrimary, fontColorNeutralSecondary, fontColorThemePrimary, previewOverflowEllipsis, textClip } from "../../../../utilities/Mixin.styles";
import { checklistItemCheckmarkIconWidth } from "../../checklist/ChecklistItem.styles";
// Utilities
import { numberToPx, pxToNumber } from "@ms/uno-utilities/lib/local/StyleUtilities";
import { isLoopTheme } from "../../../../utilities/ThemingUtilities";
/**
 * Task Card Style constants
 */ const labelMargin = "6px";
const contentPadding = "12px";
const oldContentPadding = "15px";
const oldTitleSpacing = "-5px";
const contentRowSpacing = "10px";
const titleSpacing = "-1px";
const previewSpacing = "10px";
const previewIconSize = "16px";
const previewIconToCaptionSpacing = "11px";
const checklistIconToCaptionSpacing = "11px";
const descriptionLeftSpacing = "28px";
const previewIconLeftSpacing = "1px";
const checklistIconLeftSpacing = "1px";
const checklistItemHeight = "16px";
const checklistItemSpacing = "3px 0px 3px 3px";
const taskCompleteButtonSize = "18px";
const taskCompleteButtonSpacing = "10px";
const taskTitleWidth = "236px";
const taskTitleWidthLoop = numberToPx(pxToNumber(taskTitleWidth) - pxToNumber(taskCompleteButtonSize));
const indicatorRowSpacing = "16px";
const indicatorRowHeight = "20px";
const indicatorRowIconLabelSpacing = "2px"; // The spacing between the icon and it's label (if one exists)
const indicatorRowItemSpacing = "8px"; // The spacing between the indicators (label and icon pair)
const indicatorRowItemPadding = "2px"; // Padding around indicator (label and icon) for hover state. If you remove from this, be sure to add to indicatorRowItemSpacing and vice versa
const previewCaptionMinHeight = "16px"; // Min height for the reference preview caption
const previewImageMinSquareSize = "85px"; // Minimum size of the image preview
const previewThumbnailHeight = "162px"; // This is the height generated for PPT previews and seems to be the minimum
const fallBackPreviewImageIconSize = "32px"; // Minimum size of the image preview
const assignmentRight = "12px";
const bottomBarLeftSectionLeftPadding = "12px";
const bottomBarRightSectionTopMargin = "6px";
const dueDateIndicatorTopBottomPadding = "6px";
const membersAreaTopBottomPadding = "8px";
const membersAreaLeftRightPadding = "14px"; // Note: 15px in Firefox doesn't fit 6 user bubbles per line
/**
 * Get agent status indicator section styles
 */ export const customAgentStatusIndicatorStyles = memoizeFunction(()=>{
    return {
        agentStatusIndicatorStyle: [
            flexParent(),
            flexParentCenterCrossAxis,
            {
                fontWeight: FontWeights.semibold
            }
        ]
    };
});
export const getStyles = ({ theme, isComplete, assigneeHighlighted, isChecklistDisabled })=>{
    const isLoop = isLoopTheme(theme);
    const root = [
        "taskCard",
        flexParent("row", "nowrap"),
        {
            cursor: "pointer",
            ":hover": [
                {
                    ".bottomBar .ms-Facepile-addButton": {
                        opacity: "1"
                    }
                }
            ],
            "> .container": [
                CardShadow,
                backgroundColorNeutralPrimary(theme),
                flexChildSizeFixed(),
                flexParent("row", "nowrap"),
                {
                    minHeight: "10px",
                    borderRadius: PlannerBorderRadius,
                    width: "100%",
                    "> .textContent": flexChildSizeFlexible()
                },
                theme.name === PlannerTheme.HighContrast && {
                    border: `1px solid ${Colors.White}`,
                    ":hover": {
                        borderColor: Colors.TeamsContrastHyperlinkColor
                    }
                }
            ]
        }
    ];
    const textContent = [
        "textContent",
        flexParent("column"),
        highContrastBorders(),
        {
            cursor: "pointer",
            position: "relative",
            borderRadius: isLoop ? LoopBorderRadius : `${PlannerBorderRadius} ${PlannerBorderRadius} 0 0`,
            ":hover": [
                {
                    // If the task card is hovered, show the more options button
                    "> .taskMenuSection > .taskContextMenu": [
                        {
                            opacity: "1"
                        },
                        isLoop && [
                            backgroundColorNeutralSecondary(theme),
                            {
                                ".plannerMore > span": highContrastHighlightColor()
                            }
                        ]
                    ]
                }
            ],
            "> .labels": [
                flexChildSizeFlexible("auto"),
                flexParent("row"),
                {
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    padding: contentPadding,
                    paddingBottom: "0px",
                    marginBottom: `-${labelMargin}`,
                    "> :last-child": {
                        marginRight: "0px"
                    },
                    "> li": {
                        marginRight: labelMargin,
                        marginBottom: labelMargin
                    }
                }
            ],
            "> .topBar": [
                flexChildSizeFlexible("auto"),
                flexParent("column"),
                {
                    justifyContent: "space-between",
                    padding: contentPadding,
                    "> .titleRow": getTitleRowStyles(theme, isComplete),
                    "> .planName": getPlanNameStyles(theme),
                    "> .bucketName": getBucketNameStyles(theme)
                }
            ]
        },
        isComplete && [
            theme.name === PlannerTheme.HighContrast && {
                backgroundColor: Colors.Black
            }
        ],
        assigneeHighlighted && assigneeHighlightedStyles(theme)
    ];
    const taskMenuSection = [
        "taskMenuSection",
        flexParent(),
        flexChildSizeFixed("auto"),
        fontColorNeutralSecondary(theme),
        {
            fontSize: FontSizes.small,
            right: "2px",
            top: "12px",
            lineHeight: indicatorRowHeight,
            letterSpacing: "0.75px",
            alignItems: "flex-start",
            margin: `0px ${indicatorRowIconLabelSpacing}`,
            position: "absolute"
        }
    ];
    const thumbnail = [
        "thumbnail",
        flexChildSizeFixed(previewThumbnailHeight),
        flexParent(),
        flexParentCenterAll,
        {
            textAlign: "center",
            wordWrap: "break-word",
            overflow: "hidden",
            borderRadius: `${PlannerBorderRadius} ${PlannerBorderRadius} 0 0`,
            "> .fallbackPreviewIcon": [
                {
                    fontSize: fallBackPreviewImageIconSize,
                    animation: `${fadeInOnLoad} 1s`,
                    "> svg": {
                        height: fallBackPreviewImageIconSize,
                        width: fallBackPreviewImageIconSize
                    }
                },
                theme.name === PlannerTheme.HighContrast && {
                    color: Colors.White
                }
            ],
            "> .previewImage > img.is-loaded": {
                // Fabric adds is-loaded class when image loaded
                maxWidth: "100%",
                minWidth: previewImageMinSquareSize,
                minHeight: previewImageMinSquareSize,
                pointerEvents: "none"
            },
            "&.real": [
                {
                    borderBottom: "1px solid",
                    borderColor: theme.palette.neutralLight,
                    backgroundColor: theme.palette.neutralLighterAlt
                }
            ],
            "&.placeholder": [
                {
                    borderBottom: "1px solid",
                    borderColor: theme.palette.neutralLight,
                    backgroundColor: theme.palette.neutralLighterAlt
                }
            ]
        },
        isComplete && theme.name === PlannerTheme.HighContrast && {
            "& .real": {
                backgroundColor: Colors.Black
            }
        }
    ];
    const preview = [
        "preview",
        flexParent(),
        {
            marginTop: previewSpacing,
            fontSize: FontSizes.size16,
            "> *": {
                flex: "0 1 auto",
                maxWidth: "100%"
            },
            "> .notesPreview": [
                fontColorNeutralSecondary(theme),
                previewOverflowEllipsis(),
                {
                    fontSize: FontSizes.small,
                    wordWrap: "normal",
                    maxHeight: "80px",
                    marginLeft: descriptionLeftSpacing
                },
                theme.name === PlannerTheme.HighContrast && {
                    color: Colors.White
                }
            ],
            "> .referencePreviewDescription": [
                flexParent(),
                {
                    alignItems: "center",
                    "> .previewIcon": [
                        flexChildSizeFixed(),
                        {
                            marginRight: previewIconToCaptionSpacing,
                            marginLeft: previewIconLeftSpacing,
                            padding: "0",
                            height: previewIconSize,
                            width: previewIconSize,
                            "> .ms-Image > img": {
                                height: previewIconSize,
                                width: previewIconSize
                            }
                        },
                        theme.name === PlannerTheme.HighContrast && {
                            color: Colors.White
                        }
                    ],
                    "> .previewCaption": [
                        textClip(),
                        {
                            fontSize: FontSizes.small,
                            flex: "0 1 auto",
                            minHeight: previewCaptionMinHeight
                        }
                    ],
                    ":hover > .previewCaption": [
                        {
                            textDecoration: "underline"
                        },
                        theme.name === PlannerTheme.HighContrast && {
                            color: Colors.TeamsContrastHyperlinkColor
                        }
                    ]
                }
            ],
            "> .checklistPreview": getChecklistPreviewStyles(theme, isChecklistDisabled)
        }
    ];
    const indicatorRow = [
        "indicatorRow",
        flexParent(),
        flexChildSizeFixed("auto"),
        fontColorNeutralSecondary(theme),
        {
            fontSize: FontSizes.size12,
            lineHeight: indicatorRowHeight,
            marginTop: indicatorRowSpacing,
            marginBottom: "-4px",
            letterSpacing: "0.75px",
            "> *": [
                flexParent(),
                {
                    alignItems: "center",
                    margin: `0px -${numberToPx(pxToNumber(indicatorRowItemSpacing) / 2 + pxToNumber(indicatorRowItemPadding))}`,
                    "> .indicator": [
                        flexParent(),
                        flexChildSizeFixed(),
                        {
                            margin: `0px ${numberToPx(pxToNumber(indicatorRowItemSpacing) / 2 - pxToNumber(indicatorRowIconLabelSpacing))}`,
                            padding: `0px ${indicatorRowItemPadding}`
                        }
                    ]
                }
            ],
            "> .leftSection": [
                flexChildSizeFlexible()
            ]
        }
    ];
    const topBar = [
        "topBar",
        flexParent(),
        {
            "> .bucketName.disableUpdateBucket": {
                cursor: "default",
                "> a:hover": {
                    backgroundColor: "transparent"
                }
            }
        }
    ];
    const bottomBar = [
        "bottomBar",
        flexParent("row"),
        {
            "&.noDisplayBottomBar": [
                {
                    display: "none"
                }
            ],
            transition: "color 0.167s ease-in-out, background 0.167s ease-in-out",
            borderTop: "1px solid",
            borderRadius: `0 0 ${PlannerBorderRadius} ${PlannerBorderRadius}`,
            borderColor: theme.palette.neutralLight,
            "> .completedAssignment": [
                flexChildSizeFlexible(),
                {
                    padding: `${membersAreaTopBottomPadding} ${membersAreaLeftRightPadding}`,
                    width: "100%",
                    ".ms-Persona-primaryText": [
                        fontColorNeutralPrimary(theme)
                    ]
                }
            ],
            "> .bottomBarLeftSection": [
                flexChildSizeFlexible(),
                flexParent(),
                {
                    paddingLeft: bottomBarLeftSectionLeftPadding,
                    " > .dueDateIndicator": {
                        margin: `${dueDateIndicatorTopBottomPadding} 0px`
                    }
                }
            ],
            "> .bottomBarRightSection": [
                flexParent(),
                {
                    marginRight: assignmentRight,
                    marginTop: bottomBarRightSectionTopMargin
                }
            ]
        },
        isComplete && theme.name === PlannerTheme.HighContrast && [
            {
                backgroundColor: Colors.Black,
                color: Colors.White,
                borderTop: `1px solid ${Colors.NeutralLighter}`,
                ":hover": {
                    backgroundColor: Colors.Black,
                    color: Colors.White
                }
            }
        ],
        assigneeHighlighted && assigneeHighlightedStyles(theme)
    ];
    return {
        root,
        textContent,
        taskMenuSection,
        thumbnail,
        preview,
        indicatorRow,
        topBar,
        bottomBar,
        subComponentStyles: {
            taskContextMenu: getTaskContextMenuStyles
        }
    };
};
/**
 * Helper to get the Plan Name styles
 * @param theme Planner theme
 */ const getPlanNameStyles = (theme)=>{
    return [
        flexChildSizeFixed(),
        textClip(),
        fontColorThemePrimary(theme),
        {
            fontSize: FontSizes.small,
            fontWeight: FontWeights.semibold,
            marginBottom: oldContentPadding,
            marginTop: oldTitleSpacing,
            textOverflow: "ellipsis",
            wordWrap: "break-word",
            minHeight: "16px"
        }
    ];
};
/**
 * Helper to get the Bucket Name styles
 * @param theme Planner theme
 */ const getBucketNameStyles = (theme)=>{
    const isLoop = isLoopTheme(theme);
    return [
        flexChildSizeFixed(),
        textClip(),
        {
            fontSize: FontSizes.small,
            fontWeight: FontWeights.semibold,
            marginTop: contentRowSpacing,
            textOverflow: "ellipsis",
            wordWrap: "break-word",
            "> a": [
                fontColorThemePrimary(theme),
                {
                    padding: "4px",
                    ":hover": [
                        backgroundColorNeutralSecondary(theme),
                        theme.name === PlannerTheme.HighContrast && {
                            backgroundColor: "transparent",
                            color: Colors.TeamsContrastHyperlinkColor
                        },
                        isLoop && backgroundColorNeutralPrimary(theme)
                    ]
                },
                clickableBorderAndColors(theme)
            ]
        }
    ];
};
/**
 * Helper to get the Title Row styles
 * @param theme Planner theme
 * @param isComplete Whether the task is completed
 */ const getTitleRowStyles = (theme, isComplete)=>{
    const isLoop = isLoopTheme(theme);
    return [
        flexChildSizeFixed(),
        flexParent("row"),
        {
            "> .completeButtonWithAnimation": [
                flexChildSizeFixed(taskCompleteButtonSize),
                {
                    marginRight: taskCompleteButtonSpacing
                }
            ],
            "> .title": [
                fontColorNeutralPrimary(theme),
                flexChildSizeFixed(isLoop ? taskTitleWidthLoop : taskTitleWidth),
                {
                    marginTop: titleSpacing,
                    fontSize: FontSizes.medium,
                    fontWeight: FontWeights.regular,
                    overflow: "hidden"
                },
                isComplete && [
                    fontColorNeutralSecondary(theme),
                    {
                        textDecoration: "line-through"
                    }
                ]
            ]
        }
    ];
};
/**
 * Helper to get styles for the Checklist Preview section
 * @param theme Planner theme
 */ const getChecklistPreviewStyles = (theme, isChecklistDisabled)=>{
    return [
        flexChildSizeFlexible(),
        {
            marginLeft: checklistIconLeftSpacing,
            "*": {
                marginTop: "0px !important",
                marginLeft: "0px !important"
            },
            "> .checklistItem": [
                flexParent(),
                textClip(),
                {
                    height: checklistItemHeight,
                    padding: checklistItemSpacing,
                    "> .clickWrapper": {
                        "> .checklistItemText": [
                            fontColorNeutralSecondary(theme),
                            {
                                maxWidth: "100%",
                                ".ms-Checkbox-text": [
                                    textClip("ellipsis"),
                                    fontColorNeutralSecondary(theme),
                                    highContrastColor(),
                                    {
                                        ":hover": [
                                            highContrastColor(),
                                            {
                                                textDecoration: "underline"
                                            }
                                        ],
                                        fontSize: FontSizes.small,
                                        lineHeight: checklistItemHeight,
                                        marginTop: "0px"
                                    },
                                    theme.name === PlannerTheme.HighContrast && {
                                        color: Colors.White
                                    }
                                ]
                            },
                            theme.name === PlannerTheme.HighContrast && {
                                color: Colors.White,
                                "> .ms-Checkbox-label > .ms-Checkbox-checkbox": {
                                    borderColor: Colors.White,
                                    "> .ms-Checkbox-checkmark": {
                                        color: Colors.TeamsContrastHyperlinkColor
                                    },
                                    "&:hover": {
                                        borderColor: Colors.TeamsContrastHyperlinkColor
                                    }
                                }
                            },
                            isChecklistDisabled && {
                                ".ms-Checkbox-label span:hover": {
                                    textDecoration: "none"
                                }
                            }
                        ]
                    }
                }
            ],
            ".ms-Checkbox-checkbox": [
                {
                    margin: "0px",
                    marginRight: `${checklistIconToCaptionSpacing} !important`,
                    borderRadius: "50%",
                    borderColor: theme.palette.neutralTertiary,
                    flex: `0 0 ${checklistItemHeight}`,
                    height: checklistItemHeight,
                    width: checklistItemHeight,
                    "> .ms-Checkbox-checkmark": {
                        width: checklistItemCheckmarkIconWidth
                    }
                }
            ]
        }
    ];
};
/**
 * Style for clickable border and colors
 */ export const clickableBorderAndColors = memoizeFunction((theme)=>{
    const isLoop = isLoopTheme(theme);
    return {
        borderRadius: isLoop ? "4px" : "2px",
        ":hover": [
            backgroundColorHover(theme),
            isLoop && [
                backgroundColorNeutralSecondaryAlt(theme),
                highContrastSelectionText(),
                {
                    "> .plannerMore": [
                        fontColorThemePrimary(theme),
                        {
                            "> span": highContrastSelectionText()
                        }
                    ],
                    ".plannerIcon > span": highContrastSelectionText()
                }
            ]
        ]
    };
});
/**
 * Style for fade in on load animation
 */ const fadeInOnLoad = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});
/**
 * Style for Assignee Highlighted background and/or border colors
 */ const assigneeHighlightedStyles = memoizeFunction((theme)=>{
    let backgroundColor = Colors.NeutralTertiary;
    switch(theme.name){
        case PlannerTheme.HighContrast:
            backgroundColor = Colors.Black;
            break;
        case PlannerTheme.Light:
        case PlannerTheme.Dark:
        default:
            break;
    }
    return {
        backgroundColor
    };
});
/** Styles for subcomponent TaskContextMenu */ const getTaskContextMenuStyles = memoizeFunction(({ theme, isMenuOpen })=>{
    const isLoop = isLoopTheme(theme);
    const root = [
        flexParent(),
        flexChildSizeFixed(),
        {
            margin: `0px ${numberToPx(pxToNumber(indicatorRowItemSpacing) / 2 - pxToNumber(indicatorRowIconLabelSpacing))}`,
            padding: `0px ${indicatorRowItemPadding}`,
            opacity: "0",
            ":focus": {
                opacity: "1"
            }
        },
        clickableBorderAndColors(theme),
        isMenuOpen && [
            {
                opacity: "1"
            },
            isLoop && {
                ":hover": [
                    backgroundColorNeutralSecondaryAlt(theme),
                    highContrastSelectionText()
                ]
            }
        ]
    ];
    return {
        root
    };
});
/**
 * Task Assignment Control Style constants specific to Task Card
 */ const assignDivHeightWidth = "24px";
const personaTextMaxWidth = "92px"; // The name width + persona coin width = 132px (half card width)
const facepileMarginBottom = "6px";
/**
 * Task Assignment Control style override to use within Task Card
 */ export const TaskCardTaskAssignmentControlStyles = memoizeFunction((canAssign, theme)=>{
    const isLoop = isLoopTheme(theme);
    return {
        root: [
            flexChildSizeFlexible(),
            {
                width: "100%"
            }
        ],
        assigneeRow: [
            canAssign && {
                ":hover .addFriendIcon": {
                    opacity: "1"
                }
            },
            {
                ".ms-Facepile-member": {
                    margin: "0px 4px",
                    marginBottom: facepileMarginBottom,
                    ".ms-Persona-primaryText": {
                        maxWidth: personaTextMaxWidth
                    }
                },
                ".ms-Facepile-addButton": {
                    marginBottom: facepileMarginBottom,
                    opacity: "0",
                    borderRadius: "50%"
                },
                ".ms-Facepile-addButton:focus": {
                    opacity: "1"
                },
                ".ms-Facepile-addButton:hover": [
                    backgroundColorNeutralSecondaryAlt(theme)
                ],
                ".ms-Facepile-addButton i": {
                    margin: "1px 2px 0px"
                },
                ".ms-Facepile-descriptiveOverflowButton": {
                    marginBottom: facepileMarginBottom
                },
                ".assignDiv .addFriendIcon": {
                    marginLeft: "4px",
                    marginRight: "0px"
                }
            }
        ],
        addFriendIcon: [
            isLoop ? fontColorNeutralPrimary(theme) : fontColorThemePrimary(theme),
            {
                marginBottom: "3px",
                backgroundColor: "transparent",
                position: "absolute",
                bottom: "0px",
                opacity: "1",
                transition: "opacity 0.167s ease-in-out"
            }
        ],
        assignDiv: {
            height: assignDivHeightWidth,
            width: assignDivHeightWidth,
            " &:hover": [
                backgroundColorNeutralSecondaryAlt(theme),
                {
                    borderRadius: "50%"
                }
            ]
        }
    };
});
